import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents";
import Snackbar from "src/components/common/Snackbar";
import { SnackbarType } from "src/types/common";
import {
  downloadReportExport,
  getReportExports,
  IExportCategoryGroup,
  runReportExportsNow,
} from "src/utils/api/exports";

const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Exports = () => {
  const [groups, setGroups] = useState<IExportCategoryGroup[]>([]);
  // Tracks in-flight downloads by file name / category key for spinners.
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const [downloadingCategory, setDownloadingCategory] = useState<string | null>(
    null
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarType>({
    severity: "success",
    message: "",
  });

  const { isLoading, isFetching, refetch } = useQuery(
    ["reportExports"],
    () => getReportExports(),
    {
      onSuccess: (res) => {
        setGroups(res?.data?.data ?? []);
      },
      onError: () => setGroups([]),
      refetchOnMount: true,
    }
  );

  // Manually triggers the same job the midnight cron runs, then reloads the list.
  const handleGenerateNow = async () => {
    setIsGenerating(true);
    try {
      const res = await runReportExportsNow();
      const results = res?.data?.data ?? [];
      const failed = results.filter((r) => !r.ok);
      setSnackbar(
        failed.length
          ? {
              severity: "warning",
              message: `Generated ${results.length - failed.length}/${
                results.length
              } exports. Failed: ${failed.map((f) => f.slug).join(", ")}`,
            }
          : {
              severity: "success",
              message: `All ${results.length} exports generated successfully.`,
            }
      );
      await refetch();
    } catch (err) {
      setSnackbar({
        severity: "error",
        message: "Failed to generate exports. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadFile = async (file: string) => {
    try {
      setDownloadingFile(file);
      await downloadReportExport(file);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Export download failed:", err);
    } finally {
      setDownloadingFile(null);
    }
  };

  // Downloads every file in a category, each saved separately (looped).
  const handleDownloadAll = async (group: IExportCategoryGroup) => {
    try {
      setDownloadingCategory(group.categoryKey);
      for (const item of group.files) {
        await downloadReportExport(item.file);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Bulk export download failed:", err);
    } finally {
      setDownloadingCategory(null);
    }
  };

  const loading = isLoading || isFetching;
  const isEmpty = !loading && groups.length === 0;

  return (
    <DashboardWrapper title="Exports" showBtn={false}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "16px",
        }}
      >
        <Button
          text="Generate Now"
          loading={isGenerating}
          disabled={isGenerating}
          onClick={handleGenerateNow}
        />
      </Box>
      {loading ? (
        <Box
          sx={{ display: "flex", justifyContent: "center", padding: "48px 0" }}
        >
          <CircularProgress />
        </Box>
      ) : isEmpty ? (
        <EmptyView
          heading="No exports yet"
          subheading="Exports are generated automatically every day at 12:00 AM, or click Generate Now to run them immediately."
          showBtn={false}
        />
      ) : (
        <Box sx={{ padding: "8px 4px" }}>
          {groups.map((group) => (
            <Accordion
              key={group.categoryKey}
              defaultExpanded={false}
              disableGutters
              sx={{ marginBottom: "8px", borderRadius: "8px" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "12px",
                    pr: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {group.categoryLabel}
                    </Typography>
                    <Chip
                      label={`${group.files.length} file${
                        group.files.length === 1 ? "" : "s"
                      }`}
                      size="small"
                    />
                  </Box>
                  <Box
                    component="span"
                    // Stop the click from toggling the accordion.
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <Button
                      text="Download all"
                      variant="outlined"
                      loading={downloadingCategory === group.categoryKey}
                      disabled={!!downloadingCategory}
                      onClick={() => handleDownloadAll(group)}
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell align="right">Download</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.files.map((item) => (
                      <TableRow key={item.file} hover>
                        <TableCell>{item.date}</TableCell>
                        <TableCell>{item.time}</TableCell>
                        <TableCell>{formatBytes(item.sizeBytes)}</TableCell>
                        <TableCell align="right">
                          <Box
                            component="button"
                            type="button"
                            disabled={downloadingFile === item.file}
                            onClick={() => handleDownloadFile(item.file)}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              cursor: "pointer",
                              border: "1px solid #d0d5dd",
                              borderRadius: "6px",
                              background: "#fff",
                              padding: "4px 12px",
                              color: "#1d7c4d",
                              fontWeight: 500,
                            }}
                          >
                            {downloadingFile === item.file ? (
                              <CircularProgress size={14} />
                            ) : (
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            )}
                            Download
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
      <Snackbar
        open={!!snackbar.message}
        message={snackbar.message}
        severity={snackbar.severity}
        resetSnackbar={() => setSnackbar({ severity: "success", message: "" })}
      />
    </DashboardWrapper>
  );
};

export default Exports;
